import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../auth/AuthContext';
import API_BASE from '../../utils/api';

function formatCurrency(cents) {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Nothing is charged when upgrading: the subscription switches to Premium right
// away and the difference for the rest of the billing period is prorated onto
// the next invoice.
export default function UpgradePremiumModal({ onClose }) {
  const { auth, setPlan } = useContext(AuthContext);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPreview() {
      try {
        const res = await fetch(`${API_BASE}/api/stripe/preview-upgrade`, {
          headers: { Authorization: `Bearer ${auth.idToken}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load pricing');
        setPreview(data);
      } catch (err) {
        setPreviewError(err.message);
      }
    }
    fetchPreview();
  }, [auth.idToken]);

  async function handleUpgrade(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/stripe/upgrade-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.idToken}`,
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upgrade');

      setPlan('premium');
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  const billingLabel = preview?.billing === 'annual' ? 'year' : 'month';
  const nextInvoice = preview?.nextInvoiceDate ? ` on ${formatDate(preview.nextInvoiceDate)}` : '';
  const amount = preview?.prorationAmount;
  const convertedSeats = preview?.convertedSeatCount || 0;

  return (
    <div className="upgrade-modal-overlay" onClick={loading ? undefined : onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Upgrade to Premium</h2>

        <p className="upgrade-modal-desc">
          InteriorFolio supports time billing for Premium subscribers. Track billable time, assign
          entries to projects, and manage workflows directly within the platform.
        </p>

        <div className="upgrade-price-row">
          {previewError ? (
            <span className="upgrade-price-error">{previewError}</span>
          ) : !preview ? (
            <span className="upgrade-price-loading">Calculating price…</span>
          ) : amount == null ? (
            <span className="upgrade-price-label">
              A prorated charge for the rest of this {billingLabel} will be added to your next
              invoice{nextInvoice}.
            </span>
          ) : amount > 0 ? (
            <>
              <span className="upgrade-price-amount">{formatCurrency(amount)}</span>
              <span className="upgrade-price-label">
                added to your next invoice{nextInvoice} for the rest of this {billingLabel}
              </span>
            </>
          ) : amount < 0 ? (
            <>
              <span className="upgrade-price-amount">{formatCurrency(-amount)}</span>
              <span className="upgrade-price-label">credited on your next invoice{nextInvoice}</span>
            </>
          ) : (
            <span className="upgrade-price-label">No extra charge for the rest of this {billingLabel}.</span>
          )}
        </div>

        {preview && !previewError && (
          <p className="upgrade-modal-desc">
            Nothing is charged today, and Premium features unlock right away.
            {convertedSeats > 0 &&
              ` ${convertedSeats} of your paid seats ${
                convertedSeats === 1 ? 'becomes an included Premium seat' : 'become included Premium seats'
              }, so you'll stop paying for ${convertedSeats === 1 ? 'it' : 'them'}.`}
          </p>
        )}

        <form onSubmit={handleUpgrade}>
          {error && <p className="upgrade-modal-error">{error}</p>}

          <div className="upgrade-modal-actions">
            <button type="button" className="upgrade-cancel-btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              className="upgrade-confirm-btn"
              disabled={loading || !preview || !!previewError}
            >
              {loading ? 'Upgrading…' : 'Upgrade Now'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
